import { useForm, FormProvider } from 'react-hook-form';
import { useContext, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import SubmissionConfirmation from '../SubmissionConfirmation';
import { SubmitRequest } from './SubmitRequest';
import { FundRequest } from '~/types/fundrequests';
import {
    achDocumentPresignedUrl,
    createFundRequest,
    fetchAnnualRates,
    fetchFundRequestCategories,
    resizeImage,
    updateFundRequest,
    uploadFile,
} from '~/services/fundrequests';
import { AdminContext } from '~/context/admin';
import { FundRequestContext } from '~/context/fundrequests';
import toast from 'react-hot-toast';

import { achDocumentPresignedUrlByCampaign, fetchMyCampaign } from '~/services/campaign';
import { CampaignContext } from '~/context/campaign';
import { AuthContext } from '~/context/auth';
import { MyCampaignResponse } from '~/types/campaign';
import { Loading } from '~/shared/components/Loading';
import { normalizeFileName } from '~/shared/utils';

type TransactionArrayName =
    | 'transactions_fundraising'
    | 'transactions_medical'
    | 'transactions_medications'
    | 'transactions_insurance'
    | 'transactions_relocation_lodging'
    | 'transactions_caregiving'
    | 'transactions_physical_therapy'
    | 'transactions_other'
    | 'transactions_travel'
    | 'transactions_food';

const FundRequestDetail = () => {
    const [submissionComplete, setSubmissionComplete] = useState(false);
    const [{ campaignId, isAdmin }, adminDispatch] = useContext(AdminContext);
    const [
        { fundRequest, expenseIndex, transactionsArray, selectedPayee },
        fundRequestDispatch,
    ] = useContext(FundRequestContext);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [showConfirmationPage, setShowConfirmationPage] = useState(false);
    const [loading, setLoading] = useState(true);
    const [fetchingCampaign, setFetchingCampaign] = useState(false);
    const mountedRef = useRef(true);
    const history = useHistory();

    const methods = useForm<FundRequest>({
        defaultValues: {
            vendor: undefined,
            new_vendor: {},
            transactions_fundraising: [],
            transactions_medical: [],
            transactions_other: [],
            transactions_medications: [],
            transactions_insurance: [],
            transactions_relocation_lodging: [],
            transactions_travel: [],
            transactions_food: [],
            transactions_caregiving: [],
            transactions_physical_therapy: [],
            note: '',
            document_id: '',
            payment_type: 'CHECK',
        },
        mode: 'all',
    });

    useEffect(() => {
        if (fundRequest?.fund_request_status?.value === 'submitted' || fundRequest?.fund_request_status?.value === 'resubmitted') {
            setSubmissionComplete(true);
        }
    }, [fundRequest]);

    // Scroll to top when showing submission confirmation
    useEffect(() => {
        if (submissionComplete || showConfirmationPage) {
            window.scrollTo(0, 0);
        }
    }, [submissionComplete, showConfirmationPage]);

    useEffect(() => {
        if (campaign && mountedRef.current) {
            setLoading(false);
        }
    }, [user, campaign]);

    useEffect(() => {
        if (!campaign || !campaign.campaignDetail) {
            if (user?.is_superuser) {
                return;
            }
            if (!fetchingCampaign) {
                setFetchingCampaign(true);
                getMyCampaign();
            }
        }
        return () => {
            mountedRef.current = false;
        };
    }, [user, campaign]);

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign(campaignId);
            if (resp.detail) {
                // Delete localStorage token
                localStorage.removeItem('token');
                campaignDispatch({
                    type: 'set_campaign',
                    payload: null,
                });
                history.push('/login');
                return;
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                const { person, role } = resp;
                userDispatch({
                    type: 'set_person',
                    person,
                    role,
                });
                setLoading(false);
            }
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };

    // Redirect non-admin users if the campaign is not in Disbursable status
    useEffect(() => {
        if (!campaign?.campaignDetail) return;
        const isDisbursable = campaign.campaignDetail.campaign_status === 'Disbursable';
        const canRequestDisbursement = campaign.canRequestDisbursement;
        if (!isAdmin && (!isDisbursable || !canRequestDisbursement)) {
            toast.error('Fund requests are only available for campaigns in Disbursable status.');
            history.push('/requests');
        }
    }, [campaign?.campaignDetail, isAdmin]);

    const getCategories = async () => {
        const resp = await fetchFundRequestCategories();
        fundRequestDispatch({
            type: 'set_categories',
            payload: resp,
        });
    };

    const getAnnualRates = async () => {
        const resp = await fetchAnnualRates();
        fundRequestDispatch({
            type: 'set_annual_rates',
            payload: resp,
        });
    };

    useEffect(() => {
        if (campaign?.campaignDetail) {
            getCategories();
            getAnnualRates();
        }
        return () => {
            fundRequestDispatch({ type: 'reset_fund_request' });
        };
    }, [campaign?.campaignDetail]);

    // Helper function to clean up date fields - convert empty strings to null
    const cleanDateFields = (data: any) => {
        const cleanedData = { ...data };

        // List of all transaction arrays that might contain date fields
        const transactionArrays = [
            'transactions_medical',
            'transactions_medications',
            'transactions_insurance',
            'transactions_relocation_lodging',
            'transactions_caregiving',
            'transactions_physical_therapy',
            'transactions_other',
            'transactions_travel',
            'transactions_food'
        ];

        transactionArrays.forEach(arrayName => {
            if (Array.isArray(cleanedData[arrayName])) {
                cleanedData[arrayName] = cleanedData[arrayName].map((transaction: any) => {
                    const cleanedTransaction = { ...transaction };

                    // Clean up common date fields
                    if (cleanedTransaction.expense_date === '') {
                        cleanedTransaction.expense_date = null;
                    }
                    if (cleanedTransaction.end_date === '') {
                        cleanedTransaction.end_date = null;
                    }
                    if (cleanedTransaction.date_of_travel === '') {
                        cleanedTransaction.date_of_travel = null;
                    }
                    if (cleanedTransaction.date_of_travel_end === '') {
                        cleanedTransaction.date_of_travel_end = null;
                    }
                    if (cleanedTransaction.start_date === '') {
                        cleanedTransaction.start_date = null;
                    }

                    return cleanedTransaction;
                });
            }
        });

        return cleanedData;
    };

    const handleSubmission = async (data: any, arg: { [key: string]: any }) => {
        if (data.clientPayee) delete data.clientPayee;

        // Clean up empty date strings before processing
        const cleanedData = cleanDateFields(data);

        // Ensure vendor field is properly formatted for the API
        if (cleanedData.vendor === '' || cleanedData.vendor === null) {
            delete cleanedData.vendor; // Remove empty vendor field instead of sending empty string
        } else if (cleanedData.vendor) {
            cleanedData.vendor = parseInt(cleanedData.vendor, 10); // Ensure it's an integer
        }

        // Ensure vendor_address field is properly formatted for the API
        if (cleanedData.vendor_address === '' || cleanedData.vendor_address === null) {
            delete cleanedData.vendor_address; // Remove empty vendor_address field
        } else if (cleanedData.vendor_address) {
            cleanedData.vendor_address = parseInt(cleanedData.vendor_address, 10); // Ensure it's an integer
        }

        // Determine whether to create a note (only if field is dirty and non-empty)
        const noteDirty = !!methods.formState?.dirtyFields?.note;
        const noteText = typeof cleanedData.note === 'string' ? cleanedData.note.trim() : '';
        const noteWasSubmitted = noteDirty && !!noteText;
        if (!noteWasSubmitted) {
            delete cleanedData.note;
        } else {
            cleanedData.note = noteText;
        }

        const {
            transactions_fundraising = [],
            transactions_medical = [],
            transactions_medications = [],
            transactions_insurance = [],
            transactions_relocation_lodging = [],
            transactions_caregiving = [],
            transactions_physical_therapy = [],
            transactions_other = [],
            transactions_food = [],
            transactions_travel = [],
            ach_info,
        } = cleanedData;

        // Check if ACH info is required but missing any required fields
        // Only validate ACH fields if payment type is ACH
        // When a payee is selected, check the vendor's ach_last_4 directly
        // This handles the case where user swaps to a vendor that already has ACH info
        const needsAchInfo = selectedPayee
            ? !selectedPayee.ach_last_4  // Use selected vendor's ACH status
            : fundRequest?.needs_ach_info;  // Fall back to saved fund request value
        if (needsAchInfo && data.payment_type === 'ACH') {
            // For new ACH requests, routing_number and account_number are required
            // For updates, they're optional (preserved from previous submission)
            const isNewAchRequest = !fundRequest?.ach_request;

            const missingAchFields = !ach_info ||
                !ach_info.account_type ||
                !ach_info.document_type ||
                !ach_info.agreement_signed ||
                (isNewAchRequest && !ach_info.routing_number) ||
                (isNewAchRequest && !ach_info.account_number) ||
                // Document is required for new submissions, not for updates where there's already a document
                (!ach_info.document && !ach_info.path);

            if (missingAchFields && fundRequest?.fund_request_status?.value === 'needs_info') {
                const missingFields = [];
                if (!ach_info?.account_type) missingFields.push("Account Type");
                if (!ach_info?.document_type) missingFields.push("Document Type");
                if (isNewAchRequest && !ach_info?.routing_number) missingFields.push("Routing Number");
                if (isNewAchRequest && !ach_info?.account_number) missingFields.push("Account Number");
                if (!ach_info?.agreement_signed) missingFields.push("Terms and Conditions agreement");
                
                const documentMissing = !ach_info?.document && !ach_info?.path;
                
                // Show specific message if only document is missing
                if (documentMissing && missingFields.length === 0) {
                    toast.error("Please upload a supporting document");
                    return;
                }
                
                if (documentMissing) missingFields.push("Supporting Document");

                toast.error(`Please fill in the required ACH fields: ${missingFields.join(", ")}`);
                return;
            }
        }
        
        // Remove confirm fields from ach_info before sending to backend
        if (ach_info) {
            delete ach_info.confirm_routing_number;
            delete ach_info.confirm_account_number;
        }
        
        // Clear ACH info if payment type is CHECK
        if (data.payment_type === 'CHECK') {
            cleanedData.ach_info = undefined;
        }

        // Flags to control backend semantics for partial item save
        if (arg?.permanentSave === false) {
            cleanedData.partial_update = true; // skip destructive deletions on server
            if (fundRequest?.fund_request_status?.value === 'needs_info') {
                cleanedData.preserve_needs_info_status = true;
            }
        }

        try {
            const transactions_default = [
                ...(Array.isArray(transactions_fundraising)
                    ? transactions_fundraising
                    : []),
                ...(Array.isArray(transactions_medical)
                    ? transactions_medical
                    : []),
                ...(Array.isArray(transactions_medications)
                    ? transactions_medications
                    : []),
                ...(Array.isArray(transactions_insurance)
                    ? transactions_insurance
                    : []),
                ...(Array.isArray(transactions_relocation_lodging)
                    ? transactions_relocation_lodging
                    : []),
                ...(Array.isArray(transactions_caregiving)
                    ? transactions_caregiving
                    : []),
                ...(Array.isArray(transactions_physical_therapy)
                    ? transactions_physical_therapy
                    : []),
                ...(Array.isArray(transactions_other)
                    ? transactions_other
                    : []),
            ];
            let updatedExpenseItem = null;
            const campaign_id = campaign.campaignDetail.campaign_id;
            if (fundRequest?.id) {
                // Check if ach document needs to be uploaded first
                if (ach_info && ach_info.document) {
                    // Normalize the filename to replace spaces and special characters
                    const normalizedFilename = normalizeFileName(ach_info.document);
                    const { fields, url } =
                        await achDocumentPresignedUrlByCampaign(
                            campaign_id,
                            normalizedFilename
                        );

                    // Resize the image
                    const resized = await resizeImage(ach_info.document);
                    const resp = await uploadFile(fields, url, resized);
                    const { key } = fields;
                    // Store the S3 key (path) and filename - backend will construct the full URL
                    ach_info.path = key;
                    ach_info.filename = normalizedFilename;
                    delete ach_info.document;
                } else if (!ach_info || (!ach_info.path && !ach_info.filename)) {
                    // Only clear ach_info if there's no existing path/filename
                    // (i.e., ACH info was never submitted or needs to be removed)
                    cleanedData.ach_info = undefined;
                }
                
                // const toastUpdating = toast.loading('Updating fund request...');
                const updateData = {
                    ...cleanedData,
                    transactions_default,
                };
                
                const updatedRequest = await updateFundRequest(
                    updateData,
                    fundRequest.id,
                    campaign_id
                );
                if (updatedRequest && 'error' in updatedRequest) {
                    throw updatedRequest.error;
                }
                if (arg?.permanentSave === false) {
                    // Prefer to locate the newly created/updated item robustly, since
                    // server may reorder arrays. Find any item with an id not present
                    // in the previous array; fallback to expenseIndex.
                    const defaultArrays: TransactionArrayName[] = [
                        'transactions_fundraising',
                        'transactions_medical',
                        'transactions_medications',
                        'transactions_insurance',
                        'transactions_relocation_lodging',
                        'transactions_other',
                        'transactions_physical_therapy',
                        'transactions_caregiving',
                    ];
                    const prevArray = (fundRequest && transactionsArray)
                        ? (fundRequest[transactionsArray as TransactionArrayName] as any[]) || []
                        : [];
                    const prevDefaultCombined = defaultArrays.flatMap((k) => (fundRequest?.[k] as any[]) || []);
                    const prevIds = new Set([...prevArray, ...prevDefaultCombined].map((i: any) => i?.id).filter(Boolean));
                    let updatedArray = (updatedRequest && transactionsArray)
                        ? (updatedRequest[transactionsArray as TransactionArrayName] as any[]) || []
                        : [];
                    if (!updatedArray || updatedArray.length === 0) {
                        // Fallback to combined default arrays or explicit transactions_default if provided
                        const combined = defaultArrays.flatMap((k) => (updatedRequest?.[k] as any[]) || []);
                        const explicitDefault = (updatedRequest as any)?.transactions_default || [];
                        updatedArray = explicitDefault.length ? explicitDefault : combined;
                    }
                    const diffItem = updatedArray.find((i: any) => i?.id && !prevIds.has(i.id));
                    updatedExpenseItem = diffItem
                        || (updatedArray && expenseIndex !== undefined
                            ? updatedArray[expenseIndex as number]
                            : null);
                    fundRequestDispatch({
                        type: 'set_fund_request_with_expense_item',
                        fundRequest: updatedRequest,
                        expenseItem: updatedExpenseItem,
                    });
                    // Keep the edit drawer open after a partial save
                    fundRequestDispatch({
                        type: 'set_edit_mode',
                        payload: true,
                    });
                    // Clear the note input if a note was submitted
                    if (noteWasSubmitted) {
                        methods.setValue('note', '', { shouldDirty: false, shouldTouch: false });
                    }
                    toast.success('Fund request updated');
                    return updatedExpenseItem;
                } else {
                    fundRequestDispatch({
                        type: 'set_fund_request',
                        payload: updatedRequest,
                    });
                    // Clear the note input if a note was submitted
                    if (noteWasSubmitted) {
                        methods.setValue('note', '', { shouldDirty: false, shouldTouch: false });
                    }
                    toast.success('Fund request updated');
                }

                // Draft saves should route to requests page, regular submissions show confirmation
                if (arg?.draft) {
                    history.push('/requests');
                } else {
                    setSubmissionComplete(true);
                }
                // toastUpdating && toast.dismiss(toastUpdating);
            } else {
                // const toastCreating = toast.loading('Creating fund request...');
                const requestData = {
                    ...cleanedData,
                    transactions_default,
                };
                
                const newRequest = await createFundRequest(
                    requestData,
                    campaign_id
                );
                if (newRequest && 'error' in newRequest) {
                    throw newRequest.error;
                }

                if (arg?.permanentSave === false) {
                    // Compute newly created item by id set difference
                    const defaultArrays: TransactionArrayName[] = [
                        'transactions_fundraising',
                        'transactions_medical',
                        'transactions_medications',
                        'transactions_insurance',
                        'transactions_relocation_lodging',
                        'transactions_other',
                        'transactions_physical_therapy',
                        'transactions_caregiving',
                    ];
                    const prevArray = (fundRequest && transactionsArray)
                        ? (fundRequest[transactionsArray as TransactionArrayName] as any[]) || []
                        : [];
                    const prevDefaultCombined = defaultArrays.flatMap((k) => (fundRequest?.[k] as any[]) || []);
                    const prevIds = new Set([...prevArray, ...prevDefaultCombined].map((i: any) => i?.id).filter(Boolean));
                    
                    let updatedArray = (newRequest && transactionsArray)
                        ? (newRequest[transactionsArray as TransactionArrayName] as any[]) || []
                        : [];
                    
                    if (!updatedArray || updatedArray.length === 0) {
                        const combined = defaultArrays.flatMap((k) => (newRequest?.[k] as any[]) || []);
                        const explicitDefault = (newRequest as any)?.transactions_default || [];
                        updatedArray = explicitDefault.length ? explicitDefault : combined;
                    }
                    
                    const diffItem = updatedArray.find((i: any) => i?.id && !prevIds.has(i.id));
                    
                    updatedExpenseItem = diffItem
                        || (updatedArray && expenseIndex !== undefined
                            ? updatedArray[expenseIndex as number]
                            : null);
                    
                    fundRequestDispatch({
                        type: 'set_fund_request_with_expense_item',
                        fundRequest: newRequest,
                        expenseItem: updatedExpenseItem,
                    });
                    // Keep the edit drawer open after a partial save
                    fundRequestDispatch({
                        type: 'set_edit_mode',
                        payload: true,
                    });
                    // Clear the note input if a note was submitted
                    if (noteWasSubmitted) {
                        methods.setValue('note', '', { shouldDirty: false, shouldTouch: false });
                    }
                    return updatedExpenseItem;
                } else {
                    fundRequestDispatch({
                        type: 'set_fund_request',
                        payload: newRequest,
                    });
                    fundRequestDispatch({
                        type: 'reset_expense_item',
                    });
                    // Clear the note input if a note was submitted
                    if (noteWasSubmitted) {
                        methods.setValue('note', '', { shouldDirty: false, shouldTouch: false });
                    }
                    toast.success('Fund request updated');
                }

                // Draft saves should route to requests page, regular submissions show confirmation
                if (arg?.draft) {
                    history.push('/requests');
                } else {
                    setSubmissionComplete(true);
                }
                // toastCreating && toast.dismiss(toastCreating);
            }
        } catch (error) {
            console.log('error: ', error);
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            } else {
                toast.error('An unknown error occurred');
            }
        }
    };

    if (!campaign || loading) {
        return <Loading />;
    }

    return (
        <div className='container__inner'>
            <FormProvider {...methods}>
                {!submissionComplete && !showConfirmationPage ? (
                    <SubmitRequest
                        setShowConfirmationPage={setShowConfirmationPage}
                        handleSubmission={(arg: { [key: string]: any }) => {
                            // Return the Promise so callers can await completion and get return value
                            return methods.handleSubmit((data) => handleSubmission(data, arg))();
                        }}
                    />
                ) : (
                    <SubmissionConfirmation
                        setShowConfirmationPage={setShowConfirmationPage}
                        setSubmissionComplete={setSubmissionComplete}
                    />
                )}
            </FormProvider>
        </div>
    );
};

export default FundRequestDetail;
